import axios, { AxiosError } from 'axios';
import { getLogger } from '../diagnostics/logger';
import { getSimManagementErrorFromAxiosError } from '.';
import { LoadDirectlyAvailableCommandTypesFilter } from '../actions/directlyAvailableCommandTypeActions';
import { HashMap } from '../constants';
import { LoadCommandStocksPayload } from '../models/payload/loadRequests';
import { BasicSearchFilter, PagedSearchFilter } from '../models/searchFilter';
import { SimInfo } from '../models/sim';
import { SimCommandCreate } from '../models/simCommand';
import { CommandTypes } from '../models/state/commandTypeState';
import { getApiBaseUrl } from '../services/helpers';

import config from '../config';
const log = getLogger('simManagement.api.commandTypeApi');

interface CompanySearchOptions {
  take: number;
  skip: number;
  searchTerm: string;
  wrapWith: {
    totalCount: boolean;
  };
}

interface QueryOptions {
  skip: number;
  take: number;
  id?: number;
  nameContains?: string;
  wrapWith?: string; // "count" | "total-count";
}

export function get(): Promise<CommandTypes> {
  const url = `${config.API_BASE_URL}api/simCommandType`;
  return new Promise<CommandTypes>((resolve, reject) => {
    axios
      .get(url)
      .then((response: any) => {
        const result = response.data as CommandTypes;
        resolve(result);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error('Failed to obtain SIM command types', simManagementError);

        reject(simManagementError);
      });
  });
}

function getAvailableCommandTypes(
  filter: LoadDirectlyAvailableCommandTypesFilter,
  url: string,
  loadingSubject: string
) {
  const forText =
    `selected stock serial numbers: ${filter.serialNumbers},` + `isSingleAtlasAccount: ${filter.isAtlasAccount}`;
  const data = {
    serialNumbers: filter.serialNumbers,
    isSingleAtlasAccount: filter.isAtlasAccount
  };
  return new Promise<number[]>((resolve, reject) => {
    axios
      .post(url, data)
      .then((response: any) => {
        const result = response.data as number[];
        resolve(result);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to obtain ${loadingSubject} for ${forText}`, simManagementError);
        reject(simManagementError);
      });
  });
}

export function getDirectlyAvailable(filter: LoadDirectlyAvailableCommandTypesFilter): Promise<number[]> {
  const url = config.API_BASE_URL + 'api/simCommandType/directly-available';
  const loadingSubject = 'directly-available command types';
  return getAvailableCommandTypes(filter, url, loadingSubject);
}

export function getAllAvailableCommandTypesForUser(payload: BasicSearchFilter): Promise<string[]> {
  const { companyId, accountId } = payload;
  const forText = `selected company: ${companyId}, account: ${accountId}`;
  const baseUrl = getApiBaseUrl(companyId, accountId);
  const url = `${baseUrl}userCommands`;
  return new Promise<string[]>((resolve, reject) => {
    axios
      .get(url)
      .then((response: any) => {
        const result = response.data as string[];
        resolve(result);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to obtain command types for ${forText}`, simManagementError);
        reject(simManagementError);
      });
  });
}

interface CommandTypeSelection extends BasicSearchFilter {
  file: File;
}

export interface SelectionRow extends SimInfo {
  selector: string;
  isValid: boolean;
  errorText: string;
  simInfo?: SimInfo;
}

export interface SelectionResult {
  commandDictionary: HashMap<SimCommandCreate>;
  validatedRows: SelectionRow[];
  isValid: boolean;
  commandType: string;
}

export function getCommandTypeSelection(payload: CommandTypeSelection): Promise<SelectionResult> {
  const { companyId, accountId, file } = payload;
  const forText = `selected company: ${companyId}, account: ${accountId}, file: ${file.name}`;
  const baseUrl = getApiBaseUrl(companyId, accountId);
  const url = `${baseUrl}selection`;
  const formData = new FormData();
  formData.append('file', file);
  return new Promise<SelectionResult>((resolve, reject) => {
    axios
      .post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((response: any) => {
        const result = response.data as SelectionResult;
        resolve(result);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to obtain command types for ${forText}`, simManagementError);
        reject(simManagementError);
      });
  });
}

export function getCommands(payload: LoadCommandStocksPayload): Promise<HashMap<SimCommandCreate>> {
  const { selectedStockSerialNumbers, commandType } = payload;
  const url = config.API_BASE_URL + 'api/simCommand';
  return new Promise<HashMap<SimCommandCreate>>((resolve, reject) => {
    axios
      .post(url, {
        serialNumbers: selectedStockSerialNumbers,
        commandType
      })
      .then((response: any) => {
        const result = response.data as HashMap<SimCommandCreate>;
        resolve(result);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(
          `Failed to obtain command types for selected stock serials: ${selectedStockSerialNumbers}`,
          simManagementError
        );

        reject(simManagementError);
      });
  });
}

export function downloadFileTemplate(payload: BasicSearchFilter) {
  const { companyId, accountId } = payload;
  const baseUrl = getApiBaseUrl(companyId, accountId);
  const url = `${baseUrl}\selectionTemplate`;
  return new Promise<HashMap<SimCommandCreate>>((resolve, reject) => {
    axios({
      method: 'get',
      url,
      headers: {
        'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      },
      responseType: 'arraybuffer'
    })
      .then((response: any) => {
        const type = response.headers['content-type'];
        const blob = new Blob([response.data], {
          type,
          encoding: 'UTF-8'
        } as BlobPropertyBag);
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = accountId
          ? `SelectTemplate_Company_${companyId}_${accountId}.xlsx`
          : `SelectTemplate_Company_${companyId}.xlsx`;
        link.click();
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Bulk-select template download failed for company ${companyId}, account ${accountId || 'null'}`);
        reject(simManagementError);
      });
  });
}

export function downloadProvisioningWizardTemplate(payload: BasicSearchFilter) {
  const { companyId, accountId } = payload;
  const baseUrl = getApiBaseUrl(companyId, accountId);
  const url = `${baseUrl}\provisionWizardTemplate`;
  return new Promise<HashMap<SimCommandCreate>>((resolve, reject) => {
    axios({
      method: 'get',
      url,
      headers: {
        'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      },
      responseType: 'arraybuffer'
    })
      .then((response: any) => {
        const type = response.headers['content-type'];
        const blob = new Blob([response.data], {
          type,
          encoding: 'UTF-8'
        } as BlobPropertyBag);
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `SelectTemplate_Company_${companyId}_${accountId}.xlsx`;
        link.click();
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Bulk-select template download failed for company ${companyId}, account ${accountId || 'null'}`);
        reject(simManagementError);
      });
  });
}
